import type { ThemeConfig } from './types';

/**
 * Color Theory Reference:
 * - All colors use HSL format: "hue saturation% lightness%"
 * - Triadic: 3 colors 120° apart on color wheel
 * - Complementary: 2 colors 180° apart
 * - Analogous: Colors adjacent on wheel (30° apart)
 * - Split-complementary: Base + 2 colors adjacent to complement
 */

/**
 * Default theme - Clean slate blue palette
 * Primary: Slate Blue (220°)
 * Uses monochromatic harmony with blue-gray tones
 */
const defaultLightColors = {
  background: '0 0% 99%',
  foreground: '220 14% 10%',
  card: '0 0% 100%',
  'card-foreground': '220 14% 10%',
  popover: '0 0% 100%',
  'popover-foreground': '220 14% 10%',
  primary: '220 70% 50%',
  'primary-foreground': '0 0% 100%',
  secondary: '220 14% 96%',
  'secondary-foreground': '220 14% 10%',
  muted: '220 14% 96%',
  'muted-foreground': '220 10% 38%',
  accent: '220 14% 96%',
  'accent-foreground': '220 14% 10%',
  destructive: '0 72% 51%',
  'destructive-foreground': '0 0% 100%',
  border: '220 13% 91%',
  input: '220 13% 91%',
  ring: '220 70% 50%',
  radius: '0.375rem',
  // Button variants
  'btn-success': '142 71% 45%',
  'btn-success-foreground': '0 0% 100%',
  'btn-danger': '0 72% 51%',
  'btn-danger-foreground': '0 0% 100%',
  'btn-warning': '38 92% 50%',
  'btn-warning-foreground': '0 0% 10%',
  'btn-info': '200 85% 45%',
  'btn-info-foreground': '0 0% 100%',
  'btn-neutral': '220 10% 50%',
  'btn-neutral-foreground': '0 0% 100%',
};

/**
 * Default Dark Theme - Refined slate blue with better depth
 * Deep backgrounds with elegant blue undertones
 * Improved layer separation for visual hierarchy
 */
const defaultDarkColors = {
  background: '222 47% 5%',             // Deep navy - rich and immersive
  foreground: '210 40% 96%',            // Crisp off-white for readability
  card: '222 35% 9%',                   // Elevated surface - clear separation
  'card-foreground': '210 40% 96%',
  popover: '222 30% 12%',               // Higher elevation popover
  'popover-foreground': '210 40% 96%',
  primary: '217 91% 60%',               // Vibrant blue - more saturated
  'primary-foreground': '222 47% 5%',
  secondary: '222 30% 14%',             // Subtle elevated surface
  'secondary-foreground': '210 40% 96%',
  muted: '222 25% 12%',                 // Soft background element
  'muted-foreground': '215 20% 62%',    // Higher contrast muted text
  accent: '222 30% 14%',
  'accent-foreground': '210 40% 96%',
  destructive: '0 72% 55%',             // Brighter red for dark bg
  'destructive-foreground': '0 0% 100%',
  border: '222 20% 18%',                // Visible but subtle borders
  input: '222 25% 14%',                 // Input background
  ring: '217 91% 60%',
  radius: '0.375rem',
  // Button variants - brighter for dark mode
  'btn-success': '142 72% 46%',
  'btn-success-foreground': '0 0% 100%',
  'btn-danger': '0 72% 55%',
  'btn-danger-foreground': '0 0% 100%',
  'btn-warning': '45 93% 47%',
  'btn-warning-foreground': '0 0% 8%',
  'btn-info': '199 89% 48%',
  'btn-info-foreground': '0 0% 100%',
  'btn-neutral': '215 14% 42%',
  'btn-neutral-foreground': '0 0% 100%',
};

export const defaultTheme: ThemeConfig = {
  name: 'default',
  light: defaultLightColors,
  dark: defaultDarkColors,
};

/**
 * Theme presets with carefully selected color harmonies
 */
export const themePresets: Record<string, ThemeConfig> = {
  default: defaultTheme,

  /**
   * Ocean Theme - Teal/Cyan palette
   * Primary: Teal (185°)
   * Triadic accent: Coral (5°)
   * Clean, professional, calming
   */
  ocean: {
    name: 'ocean',
    light: {
      background: '185 20% 98%',
      foreground: '200 25% 10%',
      card: '0 0% 100%',
      'card-foreground': '200 25% 10%',
      popover: '0 0% 100%',
      'popover-foreground': '200 25% 10%',
      primary: '185 75% 40%',         // Rich teal
      'primary-foreground': '0 0% 100%',
      secondary: '185 30% 94%',
      'secondary-foreground': '200 25% 10%',
      muted: '185 20% 95%',
      'muted-foreground': '200 15% 38%',
      accent: '5 70% 55%',            // Coral accent (triadic)
      'accent-foreground': '0 0% 100%',
      destructive: '0 72% 51%',
      'destructive-foreground': '0 0% 100%',
      border: '185 15% 90%',
      input: '185 15% 90%',
      ring: '185 75% 40%',
      radius: '0.375rem',
      // Button variants
      'btn-success': '160 70% 40%',
      'btn-success-foreground': '0 0% 100%',
      'btn-danger': '5 70% 50%',
      'btn-danger-foreground': '0 0% 100%',
      'btn-warning': '38 90% 50%',
      'btn-warning-foreground': '0 0% 10%',
      'btn-info': '185 75% 40%',
      'btn-info-foreground': '0 0% 100%',
      'btn-neutral': '200 15% 50%',
      'btn-neutral-foreground': '0 0% 100%',
    },
    /**
     * Ocean Dark - Deep sea ambiance
     * Rich cyan-teal with underwater depth
     * Clean, calming, professional
     */
    dark: {
      background: '198 55% 5%',           // Deep ocean blue - rich and immersive
      foreground: '180 25% 95%',          // Crisp cyan-tinted white
      card: '196 45% 9%',                 // Elevated surface with teal hint
      'card-foreground': '180 25% 95%',
      popover: '194 40% 12%',             // Higher elevation
      'popover-foreground': '180 25% 95%',
      primary: '178 65% 48%',             // Vibrant teal - signature color
      'primary-foreground': '198 55% 5%',
      secondary: '196 35% 14%',           // Subtle teal-tinted surface
      'secondary-foreground': '180 25% 95%',
      muted: '196 30% 12%',
      'muted-foreground': '185 20% 60%',  // Higher contrast muted text
      accent: '12 78% 58%',               // Warm coral - beautiful contrast
      'accent-foreground': '0 0% 100%',
      destructive: '0 72% 55%',
      'destructive-foreground': '0 0% 100%',
      border: '196 25% 18%',              // Visible teal-tinted border
      input: '196 30% 14%',
      ring: '178 65% 48%',
      radius: '0.375rem',
      // Button variants
      'btn-success': '160 72% 46%',
      'btn-success-foreground': '0 0% 100%',
      'btn-danger': '12 78% 55%',
      'btn-danger-foreground': '0 0% 100%',
      'btn-warning': '45 93% 47%',
      'btn-warning-foreground': '0 0% 8%',
      'btn-info': '178 65% 48%',
      'btn-info-foreground': '0 0% 100%',
      'btn-neutral': '196 18% 42%',
      'btn-neutral-foreground': '0 0% 100%',
    },
  },

  /**
   * Sunset Theme - Warm coral/amber palette
   * Primary: Coral (15°)
   * Secondary: Amber (35°) - analogous
   * Accent: Rose (345°) - split complementary
   * Warm, inviting, energetic
   */
  sunset: {
    name: 'sunset',
    light: {
      background: '30 30% 98%',
      foreground: '20 20% 10%',
      card: '0 0% 100%',
      'card-foreground': '20 20% 10%',
      popover: '0 0% 100%',
      'popover-foreground': '20 20% 10%',
      primary: '15 85% 55%',          // Coral
      'primary-foreground': '0 0% 100%',
      secondary: '35 80% 52%',        // Amber (analogous)
      'secondary-foreground': '0 0% 100%',
      muted: '30 25% 94%',
      'muted-foreground': '20 12% 38%',
      accent: '345 75% 55%',          // Rose (split-complementary)
      'accent-foreground': '0 0% 100%',
      destructive: '0 72% 51%',
      'destructive-foreground': '0 0% 100%',
      border: '30 20% 88%',
      input: '30 20% 88%',
      ring: '15 85% 55%',
      radius: '0.375rem',
      // Button variants
      'btn-success': '140 70% 42%',
      'btn-success-foreground': '0 0% 100%',
      'btn-danger': '345 75% 50%',
      'btn-danger-foreground': '0 0% 100%',
      'btn-warning': '35 85% 55%',
      'btn-warning-foreground': '0 0% 10%',
      'btn-info': '200 80% 48%',
      'btn-info-foreground': '0 0% 100%',
      'btn-neutral': '20 12% 50%',
      'btn-neutral-foreground': '0 0% 100%',
    },
    /**
     * Sunset Dark - Warm twilight atmosphere
     * Deep warm browns with rich coral/amber accents
     * Cozy, inviting, premium feel
     */
    dark: {
      background: '15 35% 5%',            // Deep warm charcoal - not muddy
      foreground: '35 30% 95%',           // Warm cream white
      card: '15 28% 9%',                  // Elevated warm surface
      'card-foreground': '35 30% 95%',
      popover: '15 24% 12%',              // Higher elevation
      'popover-foreground': '35 30% 95%',
      primary: '18 85% 55%',              // Vibrant coral - hero color
      'primary-foreground': '0 0% 100%',
      secondary: '28 85% 52%',            // Rich amber - warm secondary
      'secondary-foreground': '15 35% 5%',
      muted: '15 22% 12%',
      'muted-foreground': '25 18% 60%',   // Higher contrast warm muted text
      accent: '340 72% 58%',              // Rose pink - elegant accent
      'accent-foreground': '0 0% 100%',
      destructive: '0 72% 55%',
      'destructive-foreground': '0 0% 100%',
      border: '15 20% 18%',               // Visible warm border
      input: '15 22% 14%',
      ring: '18 85% 55%',
      radius: '0.375rem',
      // Button variants
      'btn-success': '145 72% 46%',
      'btn-success-foreground': '0 0% 100%',
      'btn-danger': '340 72% 55%',
      'btn-danger-foreground': '0 0% 100%',
      'btn-warning': '40 93% 50%',
      'btn-warning-foreground': '0 0% 8%',
      'btn-info': '199 80% 50%',
      'btn-info-foreground': '0 0% 100%',
      'btn-neutral': '20 16% 42%',
      'btn-neutral-foreground': '0 0% 100%',
    },
  },

  /**
   * Purple Theme - Rich violet palette
   * Primary: Violet (270°)
   * Accent: Gold (45°) - complementary
   * Elegant, creative, luxurious
   */
  purple: {
    name: 'purple',
    light: {
      background: '270 30% 98%',      // Light lavender bg
      foreground: '270 40% 15%',      // Dark purple text
      card: '0 0% 100%',              // White cards
      'card-foreground': '270 40% 15%',
      popover: '0 0% 100%',
      'popover-foreground': '270 40% 15%',
      primary: '270 65% 50%',         // Vibrant violet
      'primary-foreground': '0 0% 100%',
      secondary: '270 25% 94%',
      'secondary-foreground': '270 40% 15%',
      muted: '270 20% 95%',
      'muted-foreground': '270 15% 38%',
      accent: '45 80% 50%',           // Gold accent (complementary)
      'accent-foreground': '0 0% 100%',
      destructive: '0 72% 51%',
      'destructive-foreground': '0 0% 100%',
      border: '270 20% 90%',
      input: '270 20% 90%',
      ring: '270 65% 50%',
      radius: '0.375rem',
      // Button variants
      'btn-success': '145 70% 40%',
      'btn-success-foreground': '0 0% 100%',
      'btn-danger': '0 70% 50%',
      'btn-danger-foreground': '0 0% 100%',
      'btn-warning': '45 85% 52%',
      'btn-warning-foreground': '0 0% 10%',
      'btn-info': '240 70% 55%',
      'btn-info-foreground': '0 0% 100%',
      'btn-neutral': '270 15% 50%',
      'btn-neutral-foreground': '0 0% 100%',
    },
    /**
     * Purple Dark - Luxurious violet ambiance
     * Deep purple with elegant gold accents
     * Creative, premium, sophisticated
     */
    dark: {
      background: '268 50% 6%',           // Rich deep purple - luxurious
      foreground: '280 20% 96%',          // Soft lavender-tinted white
      card: '268 38% 10%',                // Elevated purple surface
      'card-foreground': '280 20% 96%',
      popover: '268 32% 14%',             // Higher elevation
      'popover-foreground': '280 20% 96%',
      primary: '267 75% 62%',             // Vibrant violet - signature
      'primary-foreground': '0 0% 100%',
      secondary: '268 28% 16%',           // Subtle purple surface
      'secondary-foreground': '280 20% 96%',
      muted: '268 25% 13%',
      'muted-foreground': '270 18% 62%',  // Higher contrast purple-tinted muted
      accent: '48 85% 55%',               // Rich gold - luxurious accent
      'accent-foreground': '268 50% 6%',
      destructive: '0 72% 55%',
      'destructive-foreground': '0 0% 100%',
      border: '268 22% 20%',              // Visible purple border
      input: '268 25% 16%',
      ring: '267 75% 62%',
      radius: '0.375rem',
      // Button variants
      'btn-success': '145 72% 46%',
      'btn-success-foreground': '0 0% 100%',
      'btn-danger': '0 72% 55%',
      'btn-danger-foreground': '0 0% 100%',
      'btn-warning': '48 90% 52%',
      'btn-warning-foreground': '0 0% 8%',
      'btn-info': '245 75% 62%',
      'btn-info-foreground': '0 0% 100%',
      'btn-neutral': '268 16% 42%',
      'btn-neutral-foreground': '0 0% 100%',
    },
  },

  /**
   * Petroleum Theme - Industry-inspired professional colors
   * Primary: Petroleum Blue (#4064a5)
   * Accent: Petroleum Red (#a11238)
   * Professional, trustworthy, industrial
   */
  petroleum: {
    name: 'petroleum',
    light: {
      background: '0 0% 99%',
      foreground: '220 15% 10%',
      card: '0 0% 100%',
      'card-foreground': '220 15% 10%',
      popover: '0 0% 100%',
      'popover-foreground': '220 15% 10%',
      primary: '217 44% 45%',         // Petroleum Blue #4064a5
      'primary-foreground': '0 0% 100%',
      secondary: '220 10% 94%',
      'secondary-foreground': '220 15% 10%',
      muted: '220 10% 96%',
      'muted-foreground': '220 10% 38%',
      accent: '345 82% 35%',          // Petroleum Red #a11238
      'accent-foreground': '0 0% 100%',
      destructive: '345 82% 35%',
      'destructive-foreground': '0 0% 100%',
      border: '220 13% 88%',
      input: '220 13% 88%',
      ring: '217 44% 45%',
      radius: '0.375rem',
      // Button variants
      'btn-success': '142 65% 40%',
      'btn-success-foreground': '0 0% 100%',
      'btn-danger': '345 82% 40%',
      'btn-danger-foreground': '0 0% 100%',
      'btn-warning': '38 90% 48%',
      'btn-warning-foreground': '0 0% 10%',
      'btn-info': '217 50% 50%',
      'btn-info-foreground': '0 0% 100%',
      'btn-neutral': '220 10% 50%',
      'btn-neutral-foreground': '0 0% 100%',
    },
    /**
     * Petroleum Dark - Industrial sophistication
     * Deep steel blue with bold red accents
     * Professional, trustworthy, powerful
     */
    dark: {
      background: '218 45% 5%',           // Deep steel blue - professional
      foreground: '210 25% 96%',          // Clean blue-tinted white
      card: '218 35% 9%',                 // Elevated steel surface
      'card-foreground': '210 25% 96%',
      popover: '218 30% 12%',             // Higher elevation
      'popover-foreground': '210 25% 96%',
      primary: '215 65% 58%',             // Vibrant petroleum blue
      'primary-foreground': '0 0% 100%',
      secondary: '218 28% 14%',           // Subtle steel surface
      'secondary-foreground': '210 25% 96%',
      muted: '218 24% 12%',
      'muted-foreground': '215 16% 60%',  // Higher contrast steel muted
      accent: '348 82% 52%',              // Bold petroleum red - signature
      'accent-foreground': '0 0% 100%',
      destructive: '348 82% 52%',
      'destructive-foreground': '0 0% 100%',
      border: '218 22% 18%',              // Visible steel border
      input: '218 24% 14%',
      ring: '215 65% 58%',
      radius: '0.375rem',
      // Button variants
      'btn-success': '145 72% 46%',
      'btn-success-foreground': '0 0% 100%',
      'btn-danger': '348 82% 52%',
      'btn-danger-foreground': '0 0% 100%',
      'btn-warning': '45 93% 47%',
      'btn-warning-foreground': '0 0% 8%',
      'btn-info': '215 70% 58%',
      'btn-info-foreground': '0 0% 100%',
      'btn-neutral': '218 14% 42%',
      'btn-neutral-foreground': '0 0% 100%',
    },
  },
};
